<template>
  <div class="space-y-4">
    <div class="mt-4 space-y-4">
      <div class="grid gap-8 md:grid-cols-12 md:items-end">
        <div class="min-w-0 md:col-span-5">
          <div v-if="isDelivery" data-testid="transfer-editable-edge-date">
            <Datepicker
              :model-value="editableEdgeDate"
              v-bind="editableDateTimePickerProps"
              :label="t('transferStartTime')"
              @update:model-value="handleEditableEdgeDateChange"
            />
          </div>
          <Datepicker
            v-else
            :model-value="connectedEdgeDateValue"
            v-bind="disabledDateTimePickerProps"
            :label="t('transferStartTime')"
          />
        </div>
        <div
          class="self-center max-sm:w-6 max-sm:justify-self-center md:col-span-2 md:max-w-10 md:pb-3"
        >
          <HorizontalArrow left right class="max-sm:rotate-90" />
        </div>
        <div class="min-w-0 md:col-span-5">
          <Datepicker
            v-if="isDelivery"
            :model-value="connectedEdgeDateValue"
            v-bind="disabledDateTimePickerProps"
            :label="t('arrivalTime')"
          />
          <div v-else data-testid="transfer-editable-edge-date">
            <Datepicker
              :model-value="editableEdgeDate"
              v-bind="editableDateTimePickerProps"
              :label="t('arrivalTime')"
              @update:model-value="handleEditableEdgeDateChange"
            />
          </div>
        </div>
      </div>
      <div class="grid gap-8 md:grid-cols-12">
        <div class="md:col-span-5">
          <OverrideDefaultValue
            v-slot="{ defaultInputBinds }"
            v-model="customTransferMinutes"
            :label="t('requiredTravelTime')"
            :default-value="calculatedTransferMinutes"
            :formatted-default-value="formatDurationMinutes(calculatedTransferMinutes)"
            :initial-overriden-value="calculatedTransferMinutes"
          >
            <DurationInput
              v-bind="defaultInputBinds"
              :model-value="defaultInputBinds.modelValue ?? 0"
            />
          </OverrideDefaultValue>
        </div>
        <div class="max-md:hidden md:col-span-2 md:max-w-10" aria-hidden="true" />
        <div class="md:col-span-5">
          <DurationInput v-model="bufferMinutes" :label="t('buffer')" />
        </div>
      </div>
    </div>
    <div class="flex justify-end gap-4">
      <CVButton variant="default" size="sm" :is-loading="isSaving" @click="handleSave">{{
        t('save')
      }}</CVButton>
      <CVButton variant="secondary" size="sm" :disabled="isSaving" @click="handleCancel">{{
        t('cancel')
      }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import HorizontalArrow from '@/components/HorizontalArrow.vue';
import Datepicker from '@/components/Datepicker.vue';
import DurationInput from '@/components/DurationInput.vue';
import OverrideDefaultValue from '@/components/OverrideDefaultValue.vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import { AllocationType } from '@carvia/ros-client-types';
import CVButton from '@/components/buttons/CVButton.vue';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { getDateTimeFormat } from '@/hooks/use-date-format';
import { formatDurationMinutes } from '@/utils/duration-input';

const props = defineProps<{
  transfer: Allocation;
  bookingAllocation: Allocation;
}>();

const emit = defineEmits<{
  (e: 'cancelled'): void;
  (e: 'saved'): void;
}>();

const { t } = useI18n();

const isDelivery = computed(() => props.transfer.type === AllocationType.TRANSFER_DELIVERY);

const connectedEdgeDate = computed(() =>
  isDelivery.value ? props.bookingAllocation.startDate : props.bookingAllocation.endDate,
);

const connectedEdgeDateValue = computed(() => new Date(connectedEdgeDate.value));

const calculatedTransferMinutes = computed(() => props.transfer.calculatedTransferMinutes ?? 0);

const effectiveTravelMinutes = computed(
  () => customTransferMinutes.value ?? calculatedTransferMinutes.value,
);

const editableEdgeMinDate = computed(() => {
  if (isDelivery.value) {
    return undefined;
  }
  return DateTime.fromISO(connectedEdgeDate.value)
    .plus({ minutes: effectiveTravelMinutes.value })
    .toJSDate();
});

const editableEdgeMaxDate = computed(() => {
  if (!isDelivery.value) {
    return undefined;
  }
  return DateTime.fromISO(connectedEdgeDate.value)
    .minus({ minutes: effectiveTravelMinutes.value })
    .toJSDate();
});

const editableDateTimePickerProps = computed(() => ({
  enableTimePicker: true,
  dateFormat: getDateTimeFormat(),
  minDate: editableEdgeMinDate.value,
  maxDate: editableEdgeMaxDate.value,
}));

const disabledDateTimePickerProps = {
  enableTimePicker: true,
  dateFormat: getDateTimeFormat(),
  disabled: true,
};

const initialBufferMinutes = props.transfer.bufferMinutes ?? 0;
const initialCustomTransferMinutes = props.transfer.customTransferMinutes ?? null;
const initialEditableEdgeDate =
  props.transfer.type === AllocationType.TRANSFER_DELIVERY
    ? props.transfer.startDate
    : props.transfer.endDate;

const editableEdgeDate = ref<Date>(new Date(initialEditableEdgeDate));
const savedEditableEdgeDate = ref<Date>(new Date(initialEditableEdgeDate));
const bufferMinutes = ref(initialBufferMinutes);
const savedBufferMinutes = ref(initialBufferMinutes);
const customTransferMinutes = ref<number | null>(initialCustomTransferMinutes);
const savedCustomTransferMinutes = ref<number | null>(initialCustomTransferMinutes);

const startDate = computed(() =>
  isDelivery.value ? editableEdgeDate.value.toISOString() : connectedEdgeDate.value,
);

const endDate = computed(() =>
  isDelivery.value ? connectedEdgeDate.value : editableEdgeDate.value.toISOString(),
);

const computeBufferMinutes = () => {
  const start = DateTime.fromISO(isDelivery.value ? startDate.value : connectedEdgeDate.value);
  const end = DateTime.fromISO(isDelivery.value ? connectedEdgeDate.value : endDate.value);
  const total = end.diff(start, 'minutes').minutes;
  return Math.max(0, Math.round(total - effectiveTravelMinutes.value));
};

const updateEditableEdgeFromBuffer = (buffer: number) => {
  const connected = DateTime.fromISO(connectedEdgeDate.value);
  editableEdgeDate.value = isDelivery.value
    ? connected.minus({ minutes: effectiveTravelMinutes.value + buffer }).toJSDate()
    : connected.plus({ minutes: effectiveTravelMinutes.value + buffer }).toJSDate();
};

const updateBufferFromEditableEdge = () => {
  bufferMinutes.value = computeBufferMinutes();
};

const clampEditableEdgeDate = (value: Date): Date => {
  const connected = DateTime.fromISO(connectedEdgeDate.value);
  const date = DateTime.fromJSDate(value);

  if (isDelivery.value) {
    const latestStart = connected.minus({ minutes: effectiveTravelMinutes.value });
    return date > latestStart ? latestStart.toJSDate() : value;
  }

  const earliestEnd = connected.plus({ minutes: effectiveTravelMinutes.value });
  return date < earliestEnd ? earliestEnd.toJSDate() : value;
};

let syncingFrom: 'date' | 'buffer' | 'travel' | null = null;

const handleEditableEdgeDateChange = (value: Date | null) => {
  if (!value || syncingFrom === 'buffer' || syncingFrom === 'travel') {
    return;
  }

  syncingFrom = 'date';
  editableEdgeDate.value = clampEditableEdgeDate(value);
  updateBufferFromEditableEdge();
  syncingFrom = null;
};

watch(bufferMinutes, (buffer) => {
  if (syncingFrom === 'date' || syncingFrom === 'travel') {
    return;
  }
  syncingFrom = 'buffer';
  updateEditableEdgeFromBuffer(buffer);
  syncingFrom = null;
});

watch(customTransferMinutes, () => {
  if (syncingFrom === 'date') {
    return;
  }
  syncingFrom = 'travel';
  updateEditableEdgeFromBuffer(bufferMinutes.value);
  syncingFrom = null;
});

watch(connectedEdgeDate, () => {
  if (syncingFrom) {
    return;
  }
  syncingFrom = 'buffer';
  updateEditableEdgeFromBuffer(bufferMinutes.value);
  syncingFrom = null;
});

const { isPending: isSaving, mutateAsync: updateAllocation } = useUpdateAllocation();

const handleSave = async () => {
  await updateAllocation({
    id: props.transfer.id,
    allocation: {
      bufferMinutes: bufferMinutes.value,
      customTransferMinutes: customTransferMinutes.value,
    },
  });
  savedEditableEdgeDate.value = new Date(editableEdgeDate.value);
  savedBufferMinutes.value = bufferMinutes.value;
  savedCustomTransferMinutes.value = customTransferMinutes.value;
  emit('saved');
};

const handleCancel = () => {
  editableEdgeDate.value = new Date(savedEditableEdgeDate.value);
  bufferMinutes.value = savedBufferMinutes.value;
  customTransferMinutes.value = savedCustomTransferMinutes.value;
  emit('cancelled');
};
</script>

<i18n lang="json">
{
  "en": {
    "requiredTravelTime": "Required Travel Time",
    "buffer": "Buffer",
    "transferStartTime": "Transfer Start Time",
    "arrivalTime": "Arrival Time"
  },
  "de": {
    "requiredTravelTime": "Benötigte Fahrtdauer",
    "buffer": "Puffer",
    "transferStartTime": "Transferstartzeit",
    "arrivalTime": "Ankunftszeit"
  },
  "es": {
    "requiredTravelTime": "Tiempo de trayecto necesario",
    "buffer": "Margen",
    "transferStartTime": "Hora de inicio del traslado",
    "arrivalTime": "Hora de llegada"
  }
}
</i18n>
